/**
 * TaglineBadge — heavy-sans editorial tag mark.
 * ---------------------------------------------------------------------------
 * Renders a short uppercase tagline (e.g. "NO FEES · PRIVATE RATES · BOOKED
 * IN MINUTES") as an inline SVG using the same letter idiom as the //ELITE
 * and SEARCH ADVISORS badges — ui-sans-serif, weight 800, letterSpacing
 * 0.12em — but non-italic, so it reads as a confident value-prop stamp
 * rather than the editorial italic of the other badges.
 *
 * Auto-estimates viewBox width from the text length so the same component
 * works for any tagline. Color follows currentColor; size via Tailwind h-*
 * classes on the wrapper (the SVG renders w-auto to preserve ratio).
 */

interface TaglineBadgeProps {
    text: string;
    className?: string;
    ariaLabel?: string;
    leadingIcon?: 'verified' | 'save-contact' | 'phone';
}

// Empirical width-per-char in viewBox units for ui-sans-serif weight 800
// at fontSize 38 with letterSpacing 0.12em. Calibrated slightly oversized
// (real chars are ~28-32 vb depending on glyph mix); the text below uses
// textLength + lengthAdjust to snap to whatever width we allot, so this
// constant just sets the SVG bbox that mx-auto / text-center will center.
const CHAR_WIDTH_VB = 30;
const VIEWBOX_HEIGHT = 28;

// viewBox units reserved for each leading icon (icon width + breathing room).
// Tuned per-icon since save-contact's plus extends further right than the
// verified seal, so it needs a wider lead to keep the same visual gap to text.
const ICON_LEAD_VB: Record<NonNullable<TaglineBadgeProps['leadingIcon']>, number> = {
    verified: 52,
    'save-contact': 64,
    phone: 52,
};

export function TaglineBadge({ text, className, ariaLabel, leadingIcon }: TaglineBadgeProps) {
    const textOffsetX = leadingIcon ? ICON_LEAD_VB[leadingIcon] : 0;
    // Mirror the icon's left padding on the right so the text centers within
    // the SVG bbox even when an icon is present. Without this, the icon would
    // visually shift the whole composition rightward inside a mx-auto parent.
    const textRightPadding = textOffsetX;
    const textWidth = Math.max(120, Math.ceil(text.length * CHAR_WIDTH_VB));
    const viewBoxWidth = textOffsetX + textWidth + textRightPadding;

    return (
        <svg
            viewBox={`0 0 ${viewBoxWidth} ${VIEWBOX_HEIGHT}`}
            fill="currentColor"
            aria-label={ariaLabel ?? text}
            overflow="visible"
            className={className}
        >
            {leadingIcon === 'verified' && (
                /* Verified seal — champagne-filled bumpy circle with checkmark
                   masked OUT so the page background shows through. */
                <g transform="translate(0, -5) scale(1.6)">
                    <defs>
                        <mask id="tagline-badge-verified-check">
                            <rect width="24" height="24" fill="white" />
                            <path d="m9 12 2 2 4-4" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        </mask>
                    </defs>
                    <path
                        d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"
                        fill="#fdfaf3"
                        mask="url(#tagline-badge-verified-check)"
                    />
                </g>
            )}
            {leadingIcon === 'save-contact' && (
                /* Save contact — champagne person silhouette + plus, matched scale
                   and color treatment to the verified seal. */
                <g transform="translate(0, -5) scale(1.6)" fill="#fdfaf3">
                    <circle cx="9" cy="7" r="4" />
                    <path d="M2 21v-2a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v2z" />
                    <rect x="20.5" y="7.5" width="2" height="7" rx="1" />
                    <rect x="18" y="10" width="7" height="2" rx="1" />
                </g>
            )}
            {leadingIcon === 'phone' && (
                /* Smartphone — champagne body with rounded corners + home indicator,
                   matched scale to the verified seal. */
                <g transform="translate(0, -5) scale(1.6)" fill="#fdfaf3">
                    <rect x="6" y="2" width="12" height="20" rx="2.5" ry="2.5" />
                    <circle cx="12" cy="18.5" r="0.7" fill="#0b2222" />
                </g>
            )}
            <text
                x={textOffsetX}
                y="27"
                textLength={textWidth}
                lengthAdjust="spacingAndGlyphs"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
                fontWeight="800"
                fontSize="38"
                letterSpacing="0.12em"
            >
                {text}
            </text>
        </svg>
    );
}
